/**
 * Allergy Alert Service
 * 
 * Checks if a medication may contain ingredients that match the user's
 * recorded allergies. Uses AI to analyze potential allergen matches.
 * 
 * ⚠️ CRITICAL DISCLAIMER: 
 * This is for INFORMATIONAL PURPOSES ONLY and is NOT medical advice.
 * Allergic reactions can be serious or life-threatening.
 * ALWAYS consult your doctor or pharmacist before taking any new medication.
 */

import API from '@/utils/api';

// Important disclaimer text
export const ALLERGY_DISCLAIMER = {
  short: 'For informational purposes only. Always consult your doctor or pharmacist.',
  full: 'This information is for educational purposes only and is NOT medical advice. Allergic reactions can be serious or life-threatening. ALWAYS consult your doctor or pharmacist before taking any new medication. They can review the actual medication label and your complete medical history to provide personalized guidance.',
  action: 'Please discuss with your doctor or pharmacist before taking this medication.',
  urgent: 'When in doubt, do NOT take the medication until you have spoken with a healthcare professional.',
};

// Risk levels
export const RISK_LEVEL = {
  HIGH: 'high',
  MODERATE: 'moderate',
  LOW: 'low',
  NONE: 'none',
};

// Risk level colors for UI
export const RISK_COLORS = {
  [RISK_LEVEL.HIGH]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    icon: 'text-red-600',
    header: 'bg-red-600',
  },
  [RISK_LEVEL.MODERATE]: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    icon: 'text-amber-600',
    header: 'bg-amber-500',
  },
  [RISK_LEVEL.LOW]: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    header: 'bg-yellow-500',
  },
  [RISK_LEVEL.NONE]: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    border: 'border-green-200',
    icon: 'text-green-600',
    header: 'bg-green-500',
  },
};

// Risk level labels
export const RISK_LABELS = {
  [RISK_LEVEL.HIGH]: '⚠️ High Risk - Potential Allergen Match',
  [RISK_LEVEL.MODERATE]: '⚡ Moderate Risk - Possible Cross-Reactivity',
  [RISK_LEVEL.LOW]: '📋 Low Risk - Minor Concern',
  [RISK_LEVEL.NONE]: '✓ No Known Allergen Matches Found',
};

/**
 * Fetch user's recorded allergies
 * 
 * @returns {Promise<Array>} - List of user's allergies
 */
export const getUserAllergies = async () => {
  try {
    const response = await API.get('/medical/user-allergies');
    return response.data.allergies || [];
  } catch (error) {
    console.error('[AllergyAlert] Failed to fetch allergies:', error);
    return [];
  }
};

/**
 * Check if a medication may trigger the user's allergies
 * 
 * @param {string} medicationName - Name of the medication being added
 * @param {Array} allergies - Array of user's allergy objects
 * @returns {Object} - Allergy check results
 */
export const checkAllergyAlert = async (medicationName, allergies) => {
  if (!medicationName || !allergies?.length) {
    return {
      success: true,
      hasAllergyRisk: false,
      alerts: [],
      summary: 'No allergies recorded to check against.',
      disclaimer: ALLERGY_DISCLAIMER.short,
    };
  }
  
  try {
    const response = await API.post('/medical/check-allergy-alert', {
      medication_name: medicationName,
      allergies: allergies.map(a => ({
        name: a.name,
        reaction: a.reaction,
        severity: a.severity,
      })),
    });
    
    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error('[AllergyAlert] Check failed:', error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || 'Failed to check allergens',
      hasAllergyRisk: false,
      alerts: [],
      disclaimer: ALLERGY_DISCLAIMER.short,
    };
  }
};

/**
 * Get the highest risk level from a list of alerts
 */
export const getHighestRiskLevel = (alerts) => {
  if (!alerts?.length) return RISK_LEVEL.NONE;
  
  const riskOrder = [RISK_LEVEL.HIGH, RISK_LEVEL.MODERATE, RISK_LEVEL.LOW, RISK_LEVEL.NONE];
  
  for (const risk of riskOrder) {
    if (alerts.some(a => a.risk_level === risk)) {
      return risk;
    }
  }
  
  return RISK_LEVEL.NONE;
};

/**
 * Check if user should be warned (high or moderate risk)
 */
export const shouldWarnUserAboutAllergy = (alerts) => {
  if (!alerts?.length) return false;
  return alerts.some(a => 
    a.risk_level === RISK_LEVEL.HIGH || a.risk_level === RISK_LEVEL.MODERATE
  );
};

/**
 * Get alert summary text
 */
export const getAlertSummary = (alerts) => {
  if (!alerts?.length) {
    return 'No known allergen matches found. However, always verify with your pharmacist.';
  }
  
  const highCount = alerts.filter(a => a.risk_level === RISK_LEVEL.HIGH).length;
  const moderateCount = alerts.filter(a => a.risk_level === RISK_LEVEL.MODERATE).length;
  const lowCount = alerts.filter(a => a.risk_level === RISK_LEVEL.LOW).length;
  
  const parts = [];
  if (highCount > 0) parts.push(`${highCount} high-risk`);
  if (moderateCount > 0) parts.push(`${moderateCount} moderate-risk`);
  if (lowCount > 0) parts.push(`${lowCount} low-risk`);
  
  return `Found ${parts.join(', ')} potential allergen concern${alerts.length > 1 ? 's' : ''}. ${ALLERGY_DISCLAIMER.action}`;
};

export default {
  checkAllergyAlert,
  getUserAllergies,
  getHighestRiskLevel,
  shouldWarnUserAboutAllergy,
  getAlertSummary,
  RISK_LEVEL,
  RISK_COLORS,
  RISK_LABELS,
  ALLERGY_DISCLAIMER,
};
